import { isEmpty } from 'lodash'
import { useEffect, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import Modal from 'rsuite/Modal'
import { useDropzone } from 'react-dropzone'
import * as setting from 'src/app/modules/siteSettings/components/settings'
import * as mediaManager from '../index'
import { MediaParams } from '../Model'

type Props = {
  open: boolean
  handleClose: () => void
  currentPath: string
  addFileData: { [key: string]: string }
  success: boolean | undefined
  params: MediaParams
  error: string
}

const UploadModal = ({
  open,
  handleClose,
  currentPath,
  addFileData,
  success,
  params,
  error,
}: Props) => {
  const dispatch = useDispatch()
  const [allFiles, setAllFiles] = useState<File[]>([])
  const [progressPercentage, setProgressPercentage] = useState<string>('')

  useEffect(() => {
    dispatch(setting.actions.getSettingType())
  }, [])

  const settingTypeData = useSelector((state: any) => state.settingType)
  const settingType = settingTypeData?.data?.setting?.find(
    (item: any) => item.name === 'allowed_file_extensions'
  )

  const defaultAcceptedFileExtensions =
    '.csv,.xlsl,.ods,.docs,.docx,.pdf,.jpg,.png,.jpeg,.gif,.ico,.webp,.xls,.xlsx,.mp3'

  const acceptedFileFormats =
    settingType?.value
      .split(',')
      .map((ext: string) => `.${ext}`)
      .join(',') ?? defaultAcceptedFileExtensions

  const acceptedFileList = acceptedFileFormats.split(',')

  // Success effect
  useEffect(() => {
    if (!isEmpty(addFileData) && success) {
      dispatch(mediaManager.actions.addFilesReset())
      dispatch(mediaManager.actions.getMediaList(params))
      setTimeout(() => {
        setProgressPercentage('')
        toast.success('Files uploaded successfully')
      }, 1000)
      setAllFiles([])
      handleClose()
    }
  }, [addFileData, success])

  // Error effect
  useEffect(() => {
    if (error) {
      setTimeout(() => setProgressPercentage(''), 1000)
      dispatch(mediaManager.actions.addFilesReset())
    }
  }, [error])

  // Dropzone with type and duplicate validation
  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    // Show toast for files rejected by dropzone (wrong type)
    rejectedFiles.forEach((file: any) => {
      toast.error(`File "${file.file.name}" is not a supported type.`)
    })

    const validFiles: File[] = []

    acceptedFiles.forEach((file) => {
      const ext = `.${file.name.split('.').pop()}`

      if (!acceptedFileList.includes(ext)) {
        toast.error(`File "${file.name}" is not a supported type.`)
      } else if (allFiles.some((f) => f.name === file.name)) {
        toast.error(`File "${file.name}" is already added.`)
      } else {
        validFiles.push(file)
      }
    })

    setAllFiles((prev) => [...prev, ...validFiles])
  }, [allFiles, acceptedFileList])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileFormats,
    multiple: true,
  })

  const removeFile = (file: File) => {
    setAllFiles(allFiles.filter((f) => f.name !== file.name))
  }

  // Options for progress tracking
  const options = {
    onUploadProgress: (progressEvent: any) => {
      const { loaded, total } = progressEvent
      let percent = Math.floor((loaded * 100) / total)
      if (percent < 100) {
        setProgressPercentage(`${percent}%`)
      } else if (percent >= 100) {
        setProgressPercentage('100%')
        setTimeout(() => {
          setProgressPercentage('')
        }, 1000)
      }
    },
  }

  const uploadAllFiles = () => {
    if (allFiles.length === 0) {
      toast.warning('No files selected to upload.')
      return
    }

    const body = new FormData()
    body.append('path', currentPath)

    allFiles.forEach((file) => {
      body.append('files[]', file, file.name)
    })

    dispatch(mediaManager.actions.addFiles(body, options))
  }

  return (
    <Modal
      className='info-modal w-75'
      open={open}
      onClose={() => {
        handleClose()
        setAllFiles([])
        setProgressPercentage('')
      }}
      enforceFocus={false}
      data-cy='modal'
    >
      <Modal.Header>
        <Modal.Title data-cy='modal-title'>Upload Files</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className='form-group'>
          {/* Dropzone */}
          <div
            {...getRootProps()}
            className={`dropzone border border-dashed rounded p-5 text-center mb-3 ${isDragActive ? 'bg-light-primary' : ''
              }`}
          >
            <input {...getInputProps()} data-cy='upload-modal-input-field' />
            {isDragActive ? <p>Drop files here...</p> : <p>Drag & drop files here, or click to select</p>}
          </div>

          {/* Selected files list */}
          <div className='dropzone-items mb-3'>
            {allFiles.map((file, i) => (
              <div
                key={i}
                className='dropzone-item d-flex justify-content-between align-items-center p-2 border rounded mb-2'
              >
                <div>
                  {file.name} <small>({(file.size / 1024).toFixed(2)} KB)</small>
                </div>
                <button className='btn btn-sm btn-danger' onClick={() => removeFile(file)}>
                  Remove
                </button>
              </div>
            ))}
          </div>

          {/* Buttons */}
          <div className='d-flex gap-2 mb-2'>
            <button className='btn btn-primary' onClick={uploadAllFiles}>
              Upload All
            </button>
            <button
              className='btn btn-secondary'
              onClick={() => {
                setAllFiles([])
                setProgressPercentage('')
              }}
            >
              Remove All
            </button>
          </div>

          {/* Progress bar */}
          <div className='progress mb-2' style={{ opacity: progressPercentage ? 1 : 0 }}>
            <div
              className='progress-bar bg-primary'
              role='progressbar'
              style={{ width: progressPercentage, opacity: progressPercentage ? 1 : 0 }}
            >
              {progressPercentage}
            </div>
          </div>

          <p className='text-danger'>
            Supported formats: <b>{acceptedFileFormats.replaceAll('.', ' ')}</b>
          </p>
          <small className='text-muted'>Max file size: 100 MB per file</small>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default UploadModal
